import * as React from 'react';
import { useState } from 'react';
import { Minus, Inbox, Check } from 'lucide-react';
import { cn } from './lib/utils';
import { DomainCard } from './DomainCard';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';
import { addToPriorityQueue } from '../../utils/api';

const DOMAIN_CATEGORY = {
  staffing: 'Staffing', quality: 'Quality', access: 'Access',
  finance: 'Finance', consumer: 'Consumer', health: 'Population Health',
  practice: 'Practice', ops: 'Operations',
};

/** Compact filled triangle — more scannable than stroke arrows at small sizes */
function TriUp({ className }) {
  return (
    <svg viewBox="0 0 10 8" fill="currentColor" className={className}>
      <path d="M5 0L10 8H0z" />
    </svg>
  );
}
function TriDown({ className }) {
  return (
    <svg viewBox="0 0 10 8" fill="currentColor" className={className}>
      <path d="M5 8L0 0h10z" />
    </svg>
  );
}

/**
 * KPI metric card with 4 size variants and alert levels.
 *
 * size="sm"  — 72px min, 18px value  — dense grids, sidebar supplementary stats
 * size="md"  — 96px min, 36px value  — default, 2×3 command center grid
 * size="lg"  — 128px min, 48px value — 2-per-row feature metrics
 * size="xl"  — 160px min, 64px serif — hero / top-of-page single stat
 *
 * alertLevel="none"     — default text color
 * alertLevel="warning"  — value in yellow, yellow accent bar
 * alertLevel="critical" — value in red, red accent bar
 *
 * Backward compat: alert={true} → alertLevel="critical"
 *
 * density="compact" | "default" | "spacious"
 *   Controls both outer padding AND internal item spacing together:
 *     compact  — p-2/3/4/5,  gap-0.5, value mt-0,   trend mt-0
 *     default  — p-3/4/5/6,  gap-1,   value mt-1,   trend mt-0.5
 *     spacious — p-4/5/6/7,  gap-2,   value mt-2,   trend mt-1
 *
 * accentPlacement="left" (default) | "top" — position of the 3px accent bar
 *
 * action={<Button>...</Button>} — pinned-to-bottom footer zone, separated by a
 *   border. Keeps the KPI content area at a consistent height regardless of
 *   action content. All cards in the same grid row will align their action zones.
 *
 * queueable — boolean, defaults to true when domain is set. Renders a hover-only
 *   Inbox icon in the top-right corner that adds the metric to the priority queue.
 *   Pre-populates with label, value, and domain. Pass queueable={false} to suppress.
 */

const SIZE_BASE = {
  sm: {
    minH:  'min-h-[72px]',
    label: 'text-[10px] font-bold text-text-muted',
    value: 'text-[16px] font-bold tracking-tight font-body leading-none',
    trend: 'text-[9px]',
    icon:  'h-2.5 w-2.5',
    showTrendText: true,
  },
  md: {
    minH:  'min-h-[96px]',
    label: 'text-[11px] font-bold text-text-muted',
    value: 'text-[30px] font-bold tracking-tight font-body leading-none',
    trend: 'text-[11px]',
    icon:  'h-3 w-3',
    showTrendText: true,
  },
  lg: {
    minH:  'min-h-[128px]',
    label: 'text-[11px] font-bold text-text-muted',
    value: 'text-[40px] font-bold tracking-tight font-body leading-none',
    trend: 'text-[11px]',
    icon:  'h-3.5 w-3.5',
    showTrendText: true,
  },
  xl: {
    minH:  'min-h-[160px]',
    label: 'text-body-sm text-text-muted',
    value: 'text-[56px] font-bold tracking-tight font-heading leading-none',
    trend: 'text-[12px] font-medium',
    icon:  'h-4 w-4',
    showTrendText: true,
  },
};

// Outer padding per density × size
const DENSITY_PADDING = {
  compact:  { sm: 'p-1.5', md: 'p-2', lg: 'p-3', xl: 'p-4' },
  default:  { sm: 'p-2',   md: 'p-3', lg: 'p-4', xl: 'p-5' },
  spacious: { sm: 'p-3',   md: 'p-4', lg: 'p-5', xl: 'p-6' },
};

// Internal item spacing per density (bundled with padding)
const DENSITY_SPACING = {
  compact:  { gap: 'gap-0.5', valuemt: 'mt-0',  trendmt: 'mt-0'   },
  default:  { gap: 'gap-1',   valuemt: 'mt-1',  trendmt: 'mt-0.5' },
  spacious: { gap: 'gap-2',   valuemt: 'mt-2',  trendmt: 'mt-1'   },
};

// Value text color per alert level
const ALERT_VALUE = {
  none:     'text-text-dark',
  warning:  'text-yellow-nn',
  critical: 'text-red-critical',
};

function KPICard({
  label,
  value,
  subLabel,
  trend,
  trendDown,
  alert = false,
  alertLevel,
  domain,
  accentColor,
  accentPlacement = 'left',
  size = 'md',
  density = 'default',
  action,
  queueable,
  className,
  children,
}) {
  const s  = SIZE_BASE[size] ?? SIZE_BASE.md;
  const sp = DENSITY_SPACING[density] ?? DENSITY_SPACING.default;
  const padding = (DENSITY_PADDING[density] ?? DENSITY_PADDING.default)[size] ?? 'p-4';
  const level = alertLevel ?? (alert ? 'critical' : 'none');
  const showTrend = trend !== undefined && trend !== null;
  const isNeutral = !showTrend;

  // Queue icon: default ON when domain is set, OFF otherwise
  const showQueue = queueable !== undefined ? queueable : !!domain;
  const [queueStatus, setQueueStatus] = useState('idle');

  const handleQueue = async (e) => {
    e.stopPropagation();
    if (queueStatus === 'loading') return;
    setQueueStatus('loading');
    try {
      await addToPriorityQueue({
        title: `Review and address: ${label}`,
        description: `${label}: ${value}`,
        category: DOMAIN_CATEGORY[domain] || 'General',
        priority: 'medium',
        estimatedTime: 30,
        sourceData: { domain, metric: label },
        dueDate: new Date().toISOString(),
        type: 'task',
      });
      setQueueStatus('success');
      setTimeout(() => setQueueStatus('idle'), 1500);
    } catch (_err) {
      setQueueStatus('error');
      setTimeout(() => setQueueStatus('idle'), 2000);
    }
  };

  return (
    <DomainCard
      domain={domain}
      accentColor={accentColor}
      accentPlacement={accentPlacement}
      alertLevel={level}
      density={density}
      className={cn(
        'flex flex-col',
        action ? 'justify-between' : 'justify-center',
        s.minH,
        padding,
        showQueue && 'group',
        className
      )}
    >
      {/* KPI content zone */}
      <div className={cn('flex flex-col', sp.gap)}>
        <span className={s.label}>{label}</span>

        <div className={cn('flex items-baseline gap-1.5', sp.valuemt)}>
          <span className={cn(s.value, ALERT_VALUE[level] ?? ALERT_VALUE.none)}>
            {value}
          </span>
          {showTrend && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium shrink-0',
                s.trend,
                trendDown ? 'text-red-critical' : 'text-green-nn'
              )}
            >
              {trendDown
                ? <TriDown className={s.icon} />
                : <TriUp className={s.icon} />}
              {s.showTrendText && trend}
            </span>
          )}
          {isNeutral && !subLabel && size !== 'sm' && (
            <span className={cn('inline-flex items-center gap-0.5 text-text-muted shrink-0', s.trend)}>
              <Minus className={s.icon} />
            </span>
          )}
        </div>

        {subLabel && (
          <span className={cn(s.trend, 'text-text-muted')}>{subLabel}</span>
        )}

        {children}
      </div>

      {/* Pinned action footer */}
      {action && (
        <div className="mt-3 pt-3 border-t border-nn-border">
          {action}
        </div>
      )}

      {/* Hover-revealed queue action (top-right corner) */}
      {showQueue && (
        <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-10">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="Add to queue"
                onClick={handleQueue}
                disabled={queueStatus === 'loading'}
                className="flex items-center justify-center p-1 rounded bg-nn-white/80 hover:bg-surface-warm transition-colors"
              >
                {queueStatus === 'success'
                  ? <Check size={14} className="text-green-nn" />
                  : <Inbox size={14} className={queueStatus === 'error' ? 'text-red-critical' : ''} style={queueStatus === 'error' ? undefined : { color: 'var(--aqua)' }} />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Queue it</TooltipContent>
          </Tooltip>
        </div>
      )}
    </DomainCard>
  );
}

export { KPICard };
